/**
 * Vendor-neutral observability provider abstraction.
 *
 * Defines the `ObservabilityProvider` interface that all observability
 * backends must implement. Call sites use `getProvider()` to obtain
 * the active provider — they never import a concrete SDK directly.
 *
 * Select the provider at runtime via the `VITE_OBSERVABILITY_PROVIDER`
 * environment variable (default: "otel"). Supported values:
 *
 * - `otel`  — OpenTelemetry SDK / Grafana Cloud (default)
 * - `none`  — no-op provider (disables error tracking)
 */

import { OTelProvider } from "./otel-provider";

export interface ObservabilityUser {
  id?: string;
  email?: string;
  username?: string;
}

export interface ObservabilityProvider {
  init(): void;
  setUser(user: ObservabilityUser): void;
  clearUser(): void;
  captureException(error: Error, context?: Record<string, unknown>): void;
  captureMessage(message: string, level?: "info" | "warning" | "error"): void;
  addBreadcrumb(message: string, category?: string, data?: Record<string, unknown>): void;
}

let _providerInstance: ObservabilityProvider | null = null;

export function getProvider(): ObservabilityProvider {
  if (_providerInstance) {
    return _providerInstance;
  }

  const providerName = import.meta.env.VITE_OBSERVABILITY_PROVIDER || "otel";

  if (providerName === "none") {
    _providerInstance = createNoOpProvider();
  } else {
    _providerInstance = new OTelProvider();
  }

  return _providerInstance;
}

function createNoOpProvider(): ObservabilityProvider {
  return {
    init() {},
    setUser() {},
    clearUser() {},
    captureException() {},
    captureMessage() {},
    addBreadcrumb() {},
  };
}
